import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await authAPI.me();
      setUser(data);
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await authAPI.login({ email, password });
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error al iniciar sesión';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async (credential) => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await authAPI.google(credential);
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error con Google';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const register = async (name, email, password) => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await authAPI.register({ name, email, password });
      return { success: true, message: data.message };
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error al registrarse';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async (token) => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await authAPI.verifyEmail(token);
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Token inválido o expirado';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch {}
    localStorage.clear();
    setUser(null);
  };

  const isAdmin = user?.role === 'admin';
  const isCrmAdmin = user?.crm_role === 'admin';

  return (
    <AuthContext.Provider value={{ user, loading, error, login, loginWithGoogle, register, verifyEmail, logout, isAdmin, isCrmAdmin, setError, reloadUser: loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
